import React from "react";
import { motion } from "framer-motion";
import { FileText, BookOpen, ArrowDown } from "lucide-react";
import { B2B } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Promotional band advertising the gated 2026 Portfolio PDF.
// Sits right above the B2B section and points to it as the unlock mechanism.
export default function PortfolioPromo() {
  const { t } = useT();
  return (
    <section
      data-testid={B2B.portfolioPromo}
      className="relative py-20 lg:py-28 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #FBF6EC 0%, #F3ECDF 100%)" }}
    >
      <img
        aria-hidden
        src="/hupla-emblem.svg"
        alt=""
        className="pointer-events-none select-none absolute -left-40 top-1/2 -translate-y-1/2 w-[720px] max-w-none opacity-[0.05]"
      />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left — copy */}
          <div className="lg:col-span-7">
            <p
              className="text-[0.7rem] font-bold uppercase text-[#128999]"
              style={{ letterSpacing: "0.34em" }}
            >
              {t("b2b.portfolioOverline")}
            </p>
            <h2 className="font-display font-black mt-5 leading-[0.95] text-[clamp(2rem,4.5vw,4rem)] text-[#001F39]">
              {t("b2b.portfolioTitle")}
            </h2>
            <p className="mt-6 max-w-xl text-lg text-[#001F39]/75 leading-relaxed">
              {t("b2b.portfolioBody")}
            </p>

            <ul className="mt-8 space-y-3">
              {[
                t("b2b.portfolioBullet1"),
                t("b2b.portfolioBullet2"),
                t("b2b.portfolioBullet3"),
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-[#001F39]/85">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#25bfcf] flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => scrollTo("b2b")}
              className="mt-10 group inline-flex items-center gap-3 rounded-full bg-[#001F39] text-white pl-6 pr-2 py-2 text-sm font-medium hover:bg-[#25bfcf] transition-all"
            >
              {t("b2b.portfolioHint")}
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#FFD53F] text-[#001F39] transition-transform group-hover:translate-y-0.5">
                <ArrowDown className="w-4 h-4" />
              </span>
            </button>
          </div>

          {/* Right — visual card */}
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: 2 }}
            whileInView={{ opacity: 1, y: 0, rotate: -2 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative mx-auto max-w-[420px] aspect-[3/4] rounded-3xl shadow-2xl overflow-hidden bg-white ring-1 ring-black/5">
              {/* Cover image — first deck page */}
              <img
                src="/deck/deck_page-01.webp"
                alt="Hupla Brand Portfolio 2026 cover"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Soft gradient overlay — keeps the cover art readable while
                  giving the card a premium editorial feel */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#001F39]/15 via-[#001F39]/50 to-[#001F39]/80" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
                <div className="w-16 h-16 rounded-full bg-[#FFD53F] flex items-center justify-center shadow-lg">
                  <BookOpen className="w-7 h-7 text-[#001F39]" />
                </div>
                <p
                  className="mt-6 text-[0.65rem] font-bold uppercase text-white/80"
                  style={{ letterSpacing: "0.3em" }}
                >
                  Print-ready PDF
                </p>
                <p className="mt-3 font-display font-black text-white text-3xl leading-tight">
                  Hupla<br />Brand Portfolio<br />2026
                </p>
                <p className="mt-4 text-xs text-white/70">
                  13 pages · 2.9 MB · PDF
                </p>
              </div>
            </div>

            {/* Sticker */}
            <div className="absolute -top-4 -left-4 rotate-[-8deg] rounded-full bg-[#E4004E] text-white text-xs font-bold px-4 py-2 shadow-lg flex items-center gap-2">
              <FileText className="w-4 h-4" />
              2026 Edition
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
